import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiMapPin, FiPhone, FiMail, FiClock, FiSend, FiCheckCircle, FiChevronDown, FiChevronUp } from 'react-icons/fi';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } };

function ContactModern() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitStatus, setSubmitStatus] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    setSubmitStatus('success');
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    setTimeout(() => setSubmitStatus(null), 5000);
  };

  const contactInfo = [
    { icon: <FiMapPin className="w-5 h-5" />, title: 'Office Address', lines: ['Bihar Lawyer Legal Services', 'Fraser Road, Near Gandhi Maidan', 'Patna, Bihar 800001'], color: 'from-primary-500 to-primary-600' },
    { icon: <FiPhone className="w-5 h-5" />, title: 'Phone', lines: ['+91 (612) 234-5678', 'Mon-Fri, 9 AM - 6 PM IST'], color: 'from-teal-500 to-teal-600' },
    { icon: <FiMail className="w-5 h-5" />, title: 'Email', lines: ['info@biharlawyer.in', 'support@biharlawyer.in'], color: 'from-accent-500 to-accent-600' },
    { icon: <FiClock className="w-5 h-5" />, title: 'Business Hours', lines: ['Mon-Fri: 9 AM - 6 PM', 'Sat: 10 AM - 4 PM', 'Sun: Closed'], color: 'from-primary-400 to-primary-500' },
  ];

  const faqs = [
    { q: 'How do I schedule a consultation?', a: 'You can schedule a consultation by clicking "Book Consultation" on our website, calling us at (123) 456-7890, or filling out the contact form on this page.' },
    { q: 'What areas of law do you specialize in?', a: 'We provide services in civil law, criminal law, family law, corporate law, property disputes, and general legal consultation.' },
    { q: 'Do you offer free consultations?', a: 'Yes! We offer a free initial consultation to discuss your legal matter and determine the best course of action for your case.' },
    { q: 'How much do your services cost?', a: "Our pricing varies depending on the complexity of your case. We'll provide a detailed quote after your initial consultation." },
    { q: 'Is my information confidential?', a: 'Absolutely. All client information is protected by attorney-client privilege and strict confidentiality agreements.' },
    { q: 'How experienced is your team?', a: 'Our team has over 25 years of combined legal experience with a proven track record of successful case outcomes.' },
  ];

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Hero */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0f1f35 0%, #1e3a5f 50%, #172e4d 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-72 h-72 rounded-full" style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.4) 0%, transparent 70%)' }} />
        </div>
        <div className="max-w-6xl mx-auto px-4 py-16 md:py-20 text-center relative">
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-3xl md:text-5xl font-bold text-white mb-4">Get In Touch</motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="text-white/50 text-lg max-w-xl mx-auto">Have questions? We're here to help. Contact us today.</motion.p>
        </div>
      </div>

      {/* Contact Info Cards */}
      <div className="max-w-6xl mx-auto px-4 -mt-10 relative z-10">
        <motion.div variants={{ visible: { transition: { staggerChildren: 0.1 } } }} initial="hidden" animate="visible" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {contactInfo.map((item, i) => (
            <motion.div key={i} variants={fadeUp} className="bg-white rounded-2xl shadow-card border border-secondary-100 p-6 text-center hover:shadow-card-hover transition-shadow">
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center text-white mx-auto mb-4`}>{item.icon}</div>
              <h3 className="font-semibold text-secondary-900 mb-2">{item.title}</h3>
              {item.lines.map((line, j) => <p key={j} className="text-sm text-secondary-500">{line}</p>)}
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Main Content: Form + Map */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Form */}
          <div className="lg:col-span-3">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-2xl shadow-card border border-secondary-100 p-8">
              <h2 className="text-2xl font-bold text-secondary-900 mb-2">Send us a Message</h2>
              <p className="text-secondary-500 text-sm mb-6">Fill out the form and we'll get back to you within 24 hours.</p>

              {submitStatus === 'success' && (
                <div className="flex items-center gap-3 p-4 rounded-xl bg-teal-50 border border-teal-200 mb-6">
                  <FiCheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0" />
                  <p className="text-sm text-teal-700">Thank you! Your message has been sent. We'll be in touch soon.</p>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div><label className="label">Full Name *</label><input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="John Doe" required className="input" /></div>
                  <div><label className="label">Email Address *</label><input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="john@example.com" required className="input" /></div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div><label className="label">Phone Number</label><input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="(123) 456-7890" className="input" /></div>
                  <div>
                    <label className="label">Subject *</label>
                    <select name="subject" value={formData.subject} onChange={handleChange} required className="select-input">
                      <option value="">Select a subject...</option>
                      <option value="consultation">Free Consultation</option>
                      <option value="civil">Civil Law Matter</option>
                      <option value="criminal">Criminal Defense</option>
                      <option value="family">Family Law</option>
                      <option value="corporate">Corporate Law</option>
                      <option value="property">Property Dispute</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
                <div><label className="label">Message *</label><textarea name="message" value={formData.message} onChange={handleChange} placeholder="Tell us about your legal matter..." rows="5" required className="textarea" /></div>
                <button type="submit" className="btn-primary py-3.5 px-8 gap-2"><FiSend className="w-4 h-4" /> Send Message</button>
                <p className="text-xs text-secondary-400">* Required fields. We typically respond within 24 business hours.</p>
              </form>
            </motion.div>
          </div>

          {/* Map / Side */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-2xl shadow-card border border-secondary-100 overflow-hidden h-64 flex items-center justify-center bg-secondary-50">
              <div className="text-center">
                <FiMapPin className="w-10 h-10 text-secondary-300 mx-auto mb-2" />
                <p className="text-sm text-secondary-400">Map Location</p>
                <p className="text-xs text-secondary-300 mt-1">123 Law Street, New York</p>
              </div>
            </div>
            <div className="bg-gradient-to-br from-primary-50 to-primary-100/50 rounded-2xl border border-primary-200/50 p-6">
              <h3 className="font-semibold text-secondary-900 mb-2">Need Urgent Help?</h3>
              <p className="text-sm text-secondary-600 mb-4">For urgent legal matters, call us directly for immediate assistance.</p>
              <a href="tel:+1234567890" className="btn-primary py-3 gap-2 w-full"><FiPhone className="w-4 h-4" /> Call Now</a>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className="max-w-4xl mx-auto px-4 pb-20">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-3">Frequently Asked Questions</h2>
          <p className="text-secondary-500">Find answers to common questions about our services</p>
        </div>
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }} className="bg-white rounded-xl border border-secondary-100 overflow-hidden">
              <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left hover:bg-secondary-50 transition-colors">
                <span className="font-medium text-secondary-900 pr-4">{faq.q}</span>
                {openFaq === i ? <FiChevronUp className="w-5 h-5 text-secondary-400 flex-shrink-0" /> : <FiChevronDown className="w-5 h-5 text-secondary-400 flex-shrink-0" />}
              </button>
              {openFaq === i && (
                <div className="px-5 pb-5 pt-0">
                  <p className="text-sm text-secondary-600 leading-relaxed">{faq.a}</p>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ContactModern;
